
import { unzipSync, zipSync, strToU8, strFromU8, type Unzipped } from "fflate";
import type { MultiplyConfig, SliceConfig } from "../../../types";
import {
  EMU_PER_POINT,
  MAX_RETRIES,
  NS_A,
  NS_P,
  NS_R,
  SLICE_SIZE,
} from "../../../const";
import { hasOffice, hasPpt, ppt } from "../../../utils/formatUtils";
import { FONT_FIELDS, applyFont, snapshotFont, type FontSnapshot } from "./fontSnapshot";

/* ── Read the saved presentation bytes ──────────────────────────── */

/** Concatenate the received slices into a single contiguous ArrayBuffer. */
function combineSlices(slices: Uint8Array[]): ArrayBuffer {
  const total = slices.reduce((s, sl) => s + sl.byteLength, 0);
  const combined = new Uint8Array(total);
  let off = 0;
  for (const sl of slices) {
    combined.set(sl, off);
    off += sl.byteLength;
  }
  return combined.buffer;
}

/**
 * Read every slice of the file in sequence, then close it and resolve with the
 * combined bytes. Extracted from `drainSlices` to keep callback nesting shallow.
 */
function readAllSlices(
  file: Office.File,
  sliceCount: number,
  resolve: (buf: ArrayBuffer) => void,
  reject: (err: Error) => void,
): void {
  const slices: Uint8Array[] = new Array(sliceCount);
  let received = 0;
  const readSlice = (idx: number): void => {
    file.getSliceAsync(idx, (sr) => {
      if (sr.status === Office.AsyncResultStatus.Failed) {
        file.closeAsync(() =>
          reject(new Error("Couldn't read the presentation. Please save the file (Ctrl+S) and try again.")),
        );
        return;
      }
      const raw = sr.value.data as number[] | Uint8Array;
      slices[idx] = raw instanceof Uint8Array ? raw : new Uint8Array(raw);
      received++;
      if (received === sliceCount) {
        file.closeAsync(() => resolve(combineSlices(slices)));
      } else {
        readSlice(idx + 1);
      }
    });
  };
  readSlice(0);
}

function drainSlices(file: Office.File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const sliceCount = file.sliceCount;
    if (sliceCount === 0) {
      file.closeAsync(() =>
        reject(new Error("Presentation appears empty. Save the file (Ctrl+S) and try again.")),
      );
      return;
    }
    readAllSlices(file, sliceCount, resolve, reject);
  });
}

/**
 * One `getFileAsync` attempt: read the compressed file (retrying transient
 * "internal" errors with backoff) and drain its slices. Extracted from
 * `getActivePresentationBytes` to keep callback nesting shallow.
 */
function attemptReadFile(
  attemptsLeft: number,
  resolve: (buf: ArrayBuffer) => void,
  reject: (err: Error) => void,
): void {
  if (!hasOffice()) {
    reject(new Error("Can't reach PowerPoint. Please reload the add-in and try again."));
    return;
  }
  Office.context.document.getFileAsync(
    Office.FileType.Compressed,
    { sliceSize: SLICE_SIZE },
    (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        const msg = result.error?.message ?? "unknown";
        const transient = msg.toLowerCase().includes("internal");
        if (attemptsLeft > 0 && transient) {
          const backoff = (MAX_RETRIES - attemptsLeft + 1) * 400;
          setTimeout(() => attemptReadFile(attemptsLeft - 1, resolve, reject), backoff);
          return;
        }
        reject(
          new Error("Couldn't read the presentation. Please save the file (Ctrl+S) and try again."),
        );
        return;
      }
      drainSlices(result.value).then(resolve, reject);
    },
  );
}

/** Read the current presentation as an ArrayBuffer, retrying transient errors. */
export function getActivePresentationBytes(): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    attemptReadFile(MAX_RETRIES, resolve, reject);
  });
}

/** Active slide index (1-based) via the common API. */
export function getActiveSlideIndex(): Promise<number> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getSelectedDataAsync(Office.CoercionType.SlideRange, (res) => {
        try {
          const slides = (res.value as { slides?: Array<{ index: number }> } | undefined)?.slides;
          resolve(slides?.[0]?.index ?? 1);
        } catch {
          resolve(1);
        }
      });
    } catch {
      resolve(1);
    }
  });
}

/* ── Locate the active slide's XML inside the .pptx zip ──────────── */

export function parseSlideFromZip(
  zip: Unzipped,
  slideIndex: number,
): { slideXml: string; slidePath: string } {
  const presFile = zip["ppt/presentation.xml"];
  if (!presFile) throw new Error("Couldn't open the presentation. Please save the file (Ctrl+S) and try again.");
  const presXml = strFromU8(presFile);
  const presDoc = new DOMParser().parseFromString(presXml, "text/xml");

  const sldIds = presDoc.getElementsByTagNameNS(NS_P, "sldId");
  if (sldIds.length === 0) throw new Error("This presentation has no slides.");
  if (slideIndex < 1 || slideIndex > sldIds.length) {
    throw new Error("Couldn't find the active slide. Please try again.");
  }

  const target = sldIds[slideIndex - 1]!;
  const rId = target.getAttributeNS(NS_R, "id") ?? target.getAttribute("r:id");
  if (!rId) throw new Error("Couldn't read the active slide. Please save the file (Ctrl+S) and try again.");

  const relsFile = zip["ppt/_rels/presentation.xml.rels"];
  if (!relsFile) throw new Error("Couldn't read the active slide. Please save the file (Ctrl+S) and try again.");
  const relsXml = strFromU8(relsFile);
  const relsDoc = new DOMParser().parseFromString(relsXml, "text/xml");
  const rels = relsDoc.getElementsByTagName("Relationship");

  let slidePath: string | null = null;
  for (const rel of rels) {
    if (rel.getAttribute("Id") === rId) {
      slidePath = "ppt/" + rel.getAttribute("Target");
      break;
    }
  }
  if (!slidePath) throw new Error("Couldn't read the active slide. Please save the file (Ctrl+S) and try again.");

  const slideFile = zip[slidePath];
  if (!slideFile) throw new Error("Couldn't read the active slide. Please save the file (Ctrl+S) and try again.");
  const slideXml = strFromU8(slideFile);
  return { slideXml, slidePath };
}

/* ── Selection context (names + slide index + bbox) ─────────────── */

/** Solid fill colour, explicit no-fill, or unknown (leave the clone's default). */
type ShapeFillSpec = { color: string } | "none" | null;
/** Visible outline, or none. */
type ShapeLineSpec = { color: string; weight: number } | null;

/** The text's uniform formatting: run font + paragraph/vertical alignment. */
interface TextFormat {
  font: FontSnapshot;
  horizontalAlignment: string | null;
  verticalAlignment: string | null;
}

/**
 * A selected shape's live properties (points). Visual properties (fill, line,
 * text, rotation, text formatting) are read from the *live* shape — not the
 * saved OOXML — so an unsaved edit like a recoloured fill or bold text is
 * reflected in the duplicate.
 */
interface SelectedShape {
  name: string;
  left: number;
  top: number;
  width: number;
  height: number;
  rotation: number;
  fill: ShapeFillSpec;
  line: ShapeLineSpec;
  text: string;
  textFormat: TextFormat;
}

/** Load the visual + text-formatting fields of a shape (call before `readLiveVisuals`). */
function loadLiveVisuals(s: PowerPoint.Shape): void {
  s.load("name,left,top,width,height,rotation");
  s.fill.load("type,foregroundColor");
  s.lineFormat.load("color,weight,visible");
  s.textFrame.load("verticalAlignment");
  s.textFrame.textRange.load("text");
  s.textFrame.textRange.font.load(FONT_FIELDS.join(","));
  s.textFrame.textRange.paragraphFormat.load("horizontalAlignment");
}

/** Build the visual spec of a live shape (must have been loaded via `loadLiveVisuals`). */
function readLiveVisuals(s: PowerPoint.Shape): {
  rotation: number;
  fill: ShapeFillSpec;
  line: ShapeLineSpec;
  text: string;
  textFormat: TextFormat;
} {
  const fillType = String(s.fill.type ?? "").toLowerCase();
  let fill: ShapeFillSpec = null;
  if (fillType === "nofill") fill = "none";
  else if (fillType === "solid") fill = { color: s.fill.foregroundColor };
  const line: ShapeLineSpec = s.lineFormat.visible
    ? { color: s.lineFormat.color, weight: s.lineFormat.weight }
    : null;
  return {
    rotation: s.rotation ?? 0,
    fill,
    line,
    text: s.textFrame.textRange.text ?? "",
    textFormat: {
      font: snapshotFont(s.textFrame.textRange.font),
      horizontalAlignment: s.textFrame.textRange.paragraphFormat.horizontalAlignment ?? null,
      verticalAlignment: s.textFrame.verticalAlignment ?? null,
    },
  };
}

interface SelectionContext {
  selectedShapes: SelectedShape[];
  slideIndex: number;
  bbox: { width: number; height: number };
}

async function getSelectedShapeContext(): Promise<SelectionContext> {
  const out: SelectionContext = {
    selectedShapes: [],
    slideIndex: 1,
    bbox: { width: 0, height: 0 },
  };
  await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items");
    await context.sync();
    for (const s of selected.items) loadLiveVisuals(s);
    await context.sync();

    let minL = Infinity;
    let minT = Infinity;
    let maxR = -Infinity;
    let maxB = -Infinity;
    for (const s of selected.items) {
      out.selectedShapes.push({
        name: s.name,
        left: s.left,
        top: s.top,
        width: s.width,
        height: s.height,
        ...readLiveVisuals(s),
      });
      minL = Math.min(minL, s.left);
      minT = Math.min(minT, s.top);
      maxR = Math.max(maxR, s.left + s.width);
      maxB = Math.max(maxB, s.top + s.height);
    }
    if (out.selectedShapes.length > 0) out.bbox = { width: maxR - minL, height: maxB - minT };
  });
  out.slideIndex = await getActiveSlideIndex();
  return out;
}

/* ── OOXML clone helpers ────────────────────────────────────────── */

/** Locate the <a:xfrm> element that carries a shape's position/size. */
function getShapeXfrm(el: Element): Element | undefined {
  if (el.localName === "grpSp") {
    const grpSpPr = el.getElementsByTagNameNS(NS_P, "grpSpPr")[0];
    return grpSpPr ? grpSpPr.getElementsByTagNameNS(NS_A, "xfrm")[0] : undefined;
  }
  const spPr = el.getElementsByTagNameNS(NS_P, "spPr")[0];
  let xfrm = spPr ? spPr.getElementsByTagNameNS(NS_A, "xfrm")[0] : undefined;
  xfrm ??= el.getElementsByTagNameNS(NS_P, "xfrm")[0] ?? el.getElementsByTagNameNS(NS_A, "xfrm")[0];
  return xfrm;
}

/**
 * Read a shape's centre point in EMU, or null if it has no explicit position.
 * The centre (not the top-left) is used to match shapes because it is the same
 * in the live API and the saved OOXML even when the shape is rotated — the API
 * reports a rotated shape's bounding-box, while the file stores the un-rotated
 * box, but both share the same centre.
 */
function getShapeCenterEmu(el: Element): { x: number; y: number } | null {
  const xfrm = getShapeXfrm(el);
  if (!xfrm) return null; // shape has no explicit transform — expected, not an error
  const off = xfrm.getElementsByTagNameNS(NS_A, "off")[0];
  if (!off) return null; // transform without an offset — expected, not an error
  const ext = xfrm.getElementsByTagNameNS(NS_A, "ext")[0];
  const x = Number.parseInt(off.getAttribute("x") ?? "0", 10);
  const y = Number.parseInt(off.getAttribute("y") ?? "0", 10);
  const cx = ext ? Number.parseInt(ext.getAttribute("cx") ?? "0", 10) : 0;
  const cy = ext ? Number.parseInt(ext.getAttribute("cy") ?? "0", 10) : 0;
  return { x: x + cx / 2, y: y + cy / 2 };
}

/**
 * Normalise a shape name to a sorted token set. The live PowerPoint API and the
 * saved OOXML disagree on preset-autoshape names — the API reports "Right Arrow
 * 1" while the file stores "Arrow: Right 1" — but the words (and the instance
 * number) are the same, so comparing order-independent token sets matches them.
 */
function nameKey(name: string): string {
  return name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(" ");
}


/** Unused top-level shape elements of `spTree`, in document order. */
function candidateShapeEls(spTree: Element, used: Set<Element>): Element[] {
  const out: Element[] = [];
  for (const node of spTree.childNodes) {
    if (node.nodeType !== 1) continue; // element nodes only
    const el = node as Element;
    if (used.has(el)) continue;
    if (!["sp", "grpSp", "cxnSp", "pic", "graphicFrame"].includes(el.localName)) continue;
    out.push(el);
  }
  return out;
}

/** The fields the matcher needs — position + name (visuals are irrelevant here). */
export type MatchTarget = Pick<SelectedShape, "name" | "left" | "top" | "width" | "height">;

/**
 * Match one live (selected) shape to its element in the slide's `<p:spTree>`.
 * Exported so the margin/line-spacing OOXML rewrites (which have to find the
 * same shapes in an exported slide part) reuse this matcher instead of
 * re-deriving the centre-point + name-token heuristics.
 */
export function findSelectedShapeInSpTree(
  spTree: Element,
  target: MatchTarget,
  used: Set<Element>,
): Element | null {
  const targetCx = (target.left + target.width / 2) * EMU_PER_POINT;
  const targetCy = (target.top + target.height / 2) * EMU_PER_POINT;
  const targetKey = nameKey(target.name);
  // A shape's own centre lands within a fraction of a point; 2pt absorbs
  // rounding while keeping distinct shapes apart.
  const POSITION_TOLERANCE_EMU = 2 * EMU_PER_POINT;

  let posBest: Element | null = null;
  let posDist = Infinity;
  let nameBest: Element | null = null;
  let nameDist = Infinity;
  for (const el of candidateShapeEls(spTree, used)) {
    const center = getShapeCenterEmu(el);
    const dist = center
      ? Math.hypot(center.x - targetCx, center.y - targetCy)
      : Number.MAX_SAFE_INTEGER;
    if (dist < posDist) {
      posDist = dist;
      posBest = el;
    }

    const cNvPr = el.getElementsByTagNameNS(NS_P, "cNvPr")[0];
    if (nameKey(cNvPr?.getAttribute("name") ?? "") === targetKey && dist < nameDist) {
      nameDist = dist;
      nameBest = el;
    }
  }

  // Trust position when it's a confident match; otherwise fall back to the name.
  if (posBest && posDist <= POSITION_TOLERANCE_EMU) return posBest;
  return nameBest;
}

/* ── Public: duplicate the selection at an offset ───────────────── */

/** OOXML preset-geometry tokens whose Office.js enum name isn't just the token
 *  with a capital first letter. Everything else maps by capitalising. */
const GEOM_OVERRIDES: Record<string, string> = {
  rect: "Rectangle",
  roundRect: "RoundRectangle",
  round1Rect: "Round1Rectangle",
  round2SameRect: "Round2SameRectangle",
  round2DiagRect: "Round2DiagonalRectangle",
  snip1Rect: "Snip1Rectangle",
  snip2SameRect: "Snip2SameRectangle",
  snip2DiagRect: "Snip2DiagonalRectangle",
  snipRoundRect: "SnipRoundRectangle",
  wedgeRectCallout: "WedgeRectCallout",
  wedgeRoundRectCallout: "WedgeRRectCallout",
  wedgeEllipseCallout: "WedgeEllipseCallout",
};

/** Map an OOXML `prst` geometry token to the Office.js GeometricShapeType. */
function prstToGeometry(prst: string): PowerPoint.GeometricShapeType {
  const name = GEOM_OVERRIDES[prst] ?? prst.charAt(0).toUpperCase() + prst.slice(1);
  return name as PowerPoint.GeometricShapeType;
}

/** A shape reduced to what Office.js can recreate natively (all lengths in points). */
interface NativeShapeSpec {
  geometry: PowerPoint.GeometricShapeType;
  left: number;
  top: number;
  width: number;
  height: number;
  rotation: number; // degrees
  fill: ShapeFillSpec;
  line: ShapeLineSpec;
  text: string;
  textFormat: TextFormat;
}

/** The OOXML preset geometry of a shape element — the one property the live API
 *  can't report — defaulting to a rectangle. */
function readGeometry(el: Element): PowerPoint.GeometricShapeType {
  return prstToGeometry(el.getElementsByTagNameNS(NS_A, "prstGeom")[0]?.getAttribute("prst") ?? "rect");
}

/** Build a native spec: geometry from the OOXML element, everything else from
 *  the live shape (so unsaved fill/text/rotation edits are honoured). */
function specFor(el: Element, live: SelectedShape): NativeShapeSpec {
  return {
    geometry: readGeometry(el),
    left: live.left,
    top: live.top,
    width: live.width,
    height: live.height,
    rotation: live.rotation,
    fill: live.fill,
    line: live.line,
    text: live.text,
    textFormat: live.textFormat,
  };
}

/** Create one native shape from a spec on `slide` — a single undoable action. */
function createNativeShape(slide: PowerPoint.Slide, spec: NativeShapeSpec): void {
  const shape = slide.shapes.addGeometricShape(spec.geometry, {
    left: spec.left,
    top: spec.top,
    width: spec.width,
    height: spec.height,
  });
  if (spec.rotation) shape.rotation = spec.rotation;
  if (spec.fill === "none") shape.fill.clear();
  else if (spec.fill) shape.fill.setSolidColor(spec.fill.color);
  if (spec.line) {
    shape.lineFormat.color = spec.line.color;
    if (spec.line.weight) shape.lineFormat.weight = spec.line.weight;
  }
  if (spec.text) {
    const range = shape.textFrame.textRange;
    range.text = spec.text;
    applyFont(range.font, spec.textFormat.font);
    if (spec.textFormat.horizontalAlignment) {
      range.paragraphFormat.horizontalAlignment =
        spec.textFormat.horizontalAlignment as PowerPoint.ParagraphHorizontalAlignment;
    }
  }
  if (spec.textFormat.verticalAlignment) {
    shape.textFrame.verticalAlignment = spec.textFormat.verticalAlignment as PowerPoint.TextVerticalAlignment;
  }
}

function toFriendlyShapeError(err: unknown): Error {
  const message = err instanceof Error ? err.message : String(err);
  const code = (err as { code?: unknown }).code;
  if (code === "InvalidArgument" || /invalid argument/i.test(message)) {
    return new Error(
      "This shape isn't supported here — grouped shapes, charts, SmartArt and images can't be duplicated or split. Try a basic shape or text box.",
    );
  }
  return err instanceof Error ? err : new Error(message);
}

/**
 * Duplicate the selected shapes, offset by (dx, dy) — each in units of the
 * selection's bounding box (so `dx = 1` places the copy one box-width to the
 * right).
 *
 * The clone is created *natively* on the same slide via `addGeometricShape` so
 * it is a single, cleanly-undoable action (one Undo removes the copy). The
 * source shape's geometry, size, rotation, solid fill and text are read from
 * the saved OOXML; gradient/picture fills, effects and grouped shapes can't be
 * reproduced this way and fall back to defaults / are skipped.
 */
/* ── Table-row surgery on an exported one-slide deck (Split Table) ─── */

/** Decode base64 → bytes (fflate has no base64). */
function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.codePointAt(i) ?? 0;
  return out;
}

/** Encode bytes → base64 in chunks (avoids String.fromCodePoint arg limits). */
function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCodePoint(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

/**
 * Remove table rows [dropFrom, dropToExclusive) from the single slide inside an
 * exported base64 deck and return a new base64 deck. Split Table uses this to
 * pre-build the "moved" slide at the OOXML level so the live split is one sync.
 * Only the first table on the slide is touched (matching `loadSlideTable`).
 */
export function removeTableRowsFromExportedSlide(
  base64Deck: string,
  dropFrom: number,
  dropToExclusive: number,
): string {
  if (dropToExclusive <= dropFrom) return base64Deck;
  const zip: Unzipped = unzipSync(base64ToBytes(base64Deck));
  const slidePath = Object.keys(zip).find((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p));
  if (!slidePath) return base64Deck;

  const doc = new DOMParser().parseFromString(strFromU8(zip[slidePath]!), "text/xml");
  const tbl = doc.getElementsByTagNameNS(NS_A, "tbl")[0];
  if (tbl) {
    const rows = Array.from(tbl.getElementsByTagNameNS(NS_A, "tr"));
    const end = Math.min(dropToExclusive, rows.length);
    for (let r = end - 1; r >= dropFrom; r--) rows[r]?.remove();
  }
  zip[slidePath] = strToU8(new XMLSerializer().serializeToString(doc));
  return bytesToBase64(zipSync(zip));
}

export async function duplicateSelection(dx: number, dy: number): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  if (!hasOffice()) throw new Error("Can't reach PowerPoint. Please reload the add-in and try again.");

  // 1) selected shapes (name + position) + slide index + bounding box (in points)
  const { selectedShapes, slideIndex, bbox } = await getSelectedShapeContext();
  if (selectedShapes.length === 0) throw new Error("Select at least one shape first.");

  // 2) read the saved .pptx and locate the active slide's XML
  const pptxData = await getActivePresentationBytes();
  const zip = unzipSync(new Uint8Array(pptxData));
  const { slideXml } = parseSlideFromZip(zip, slideIndex);

  // 3) parse the slide XML
  const slideDoc = new DOMParser().parseFromString(slideXml, "text/xml");
  if (slideDoc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Couldn't read this slide's content. Please save the file (Ctrl+S) and try again.");
  }
  const spTree = slideDoc.getElementsByTagNameNS(NS_P, "spTree")[0];
  if (!spTree) throw new Error("Couldn't read this slide's content. Please save the file (Ctrl+S) and try again.");

  // 4) match each selected shape to its XML element and read a native spec,
  //    shifted by the offset (in points).
  const offX = dx * (bbox.width + 10);
  const offY = dy * (bbox.height + 10);
  const specs: NativeShapeSpec[] = [];
  const matched = new Set<Element>();
  for (const shape of selectedShapes) {
    const sourceEl = findSelectedShapeInSpTree(spTree, shape, matched);
    if (!sourceEl) continue;
    matched.add(sourceEl);
    const spec = specFor(sourceEl, shape);
    spec.left += offX;
    spec.top += offY;
    specs.push(spec);
  }
  if (specs.length === 0) {
    throw new Error(
      "Couldn't find the selected shape on the slide. If you just added or moved it, " +
        "save (Ctrl+S) and try again. Grouped shapes aren't supported yet.",
    );
  }

  // 5) create the clones natively on the active slide — a single undoable action.
  try {
    await ppt().run(async (context) => {
      const slide = context.presentation.slides.getItemAt(slideIndex - 1);
      for (const s of specs) createNativeShape(slide, s);
      await context.sync();
    });
  } catch (err) {
    throw toFriendlyShapeError(err);
  }
}

/* ── Public: multiply + slice the selected shape into a grid ─────── */

async function getFirstSelectedShape(): Promise<SelectedShape> {
  let out: SelectedShape | null = null;
  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select a shape first.");
    if (sel.items.length > 1) {
      throw new Error("Select just one shape — Multiply and Slice work on a single shape at a time.");
    }
    const first = sel.items[0]!;
    first.load("type");
    await context.sync();
    if (String(first.type) === "Table") {
      throw new Error("Multiply and Slice don't work on tables — please select a shape instead.");
    }
    loadLiveVisuals(first);
    await context.sync();
    out = {
      name: first.name,
      left: first.left,
      top: first.top,
      width: first.width,
      height: first.height,
      ...readLiveVisuals(first),
    };
  });
  if (!out) throw new Error("Select a shape first.");
  return out;
}

async function resolveSourceGeometry(
  src: SelectedShape,
): Promise<{ slideIndex: number; geometry: PowerPoint.GeometricShapeType }> {
  const slideIndex = await getActiveSlideIndex();
  const pptxData = await getActivePresentationBytes();
  const zip = unzipSync(new Uint8Array(pptxData));
  const { slideXml } = parseSlideFromZip(zip, slideIndex);

  const slideDoc = new DOMParser().parseFromString(slideXml, "text/xml");
  if (slideDoc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Couldn't read this slide's content. Please save the file (Ctrl+S) and try again.");
  }
  const spTree = slideDoc.getElementsByTagNameNS(NS_P, "spTree")[0];
  if (!spTree) throw new Error("Couldn't read this slide's content. Please save the file (Ctrl+S) and try again.");

  const sourceEl = findSelectedShapeInSpTree(spTree, src, new Set());
  if (!sourceEl) {
    throw new Error(
      `Couldn't find "${src.name}" on the slide. Please save (Ctrl+S) and try again. ` +
        "Grouped shapes aren't supported yet.",
    );
  }
  return { slideIndex, geometry: readGeometry(sourceEl) };
}


export async function multiplySelection(cfg: MultiplyConfig): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  if (!hasOffice()) throw new Error("Can't reach PowerPoint. Please reload the add-in and try again.");

  const rows = Math.max(1, Math.floor(cfg.rows));
  const cols = Math.max(1, Math.floor(cfg.cols));
  const rowGutter = Math.max(0, cfg.rowGutter) * 72; // inches → points
  const colGutter = Math.max(0, cfg.colGutter) * 72;

  const src = await getFirstSelectedShape();
  const { slideIndex, geometry } = await resolveSourceGeometry(src);

  // Every cell is the size of the source (sameSize is a no-op for a single source).
  const cellW = src.width;
  const cellH = src.height;
  const headerOffset = cfg.addHeader ? cellH + rowGutter : 0;
  const originLeft = src.left;
  const originTop = src.top + headerOffset;

  try {
    await ppt().run(async (context) => {
      const slide = context.presentation.slides.getItemAt(slideIndex - 1);

      // The source shape becomes cell (0,0): move it down if a header row is added.
      const sel = context.presentation.getSelectedShapes();
      sel.load("items");
      await context.sync();
      const original = sel.items[0];
      if (original) {
        original.left = originLeft;
        original.top = originTop;
      }

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (r === 0 && c === 0) continue;
          createNativeShape(slide, {
            geometry,
            left: originLeft + c * (cellW + colGutter),
            top: originTop + r * (cellH + rowGutter),
            width: cellW,
            height: cellH,
            rotation: src.rotation,
            fill: src.fill,
            line: src.line,
            text: src.text,
            textFormat: src.textFormat,
          });
        }
      }

      if (cfg.addHeader) {
        const headerW = cols * cellW + (cols - 1) * colGutter;
        const header = slide.shapes.addTextBox("Header", {
          left: src.left,
          top: src.top,
          width: headerW,
          height: cellH,
        });
        header.textFrame.textRange.font.bold = true;
      }

      await context.sync();
    });
  } catch (err) {
    throw toFriendlyShapeError(err);
  }
}

/**
 * Partition the selected shape into rows × cols equal pieces (with gutters).
 * The source is resized to piece (0,0); the rest are native clones covering
 * their cell (so undo is clean).
 */
export async function sliceSelection(cfg: SliceConfig): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  if (!hasOffice()) throw new Error("Can't reach PowerPoint. Please reload the add-in and try again.");

  const rows = Math.max(1, Math.floor(cfg.rows));
  const cols = Math.max(1, Math.floor(cfg.cols));
  const rowGutter = Math.max(0, cfg.rowGutter) * 72; // inches → points
  const colGutter = Math.max(0, cfg.colGutter) * 72;

  const src = await getFirstSelectedShape();
  const { slideIndex, geometry } = await resolveSourceGeometry(src);

  const cellW = (src.width - (cols - 1) * colGutter) / cols;
  const cellH = (src.height - (rows - 1) * rowGutter) / rows;
  if (cellW <= 0 || cellH <= 0) {
    throw new Error("The gaps are too large for the shape. Reduce them and try again.");
  }

  try {
    await ppt().run(async (context) => {
      const slide = context.presentation.slides.getItemAt(slideIndex - 1);

      // Resize the original to cover piece (0,0), keeping its top-left.
      const sel = context.presentation.getSelectedShapes();
      sel.load("items");
      await context.sync();
      const original = sel.items[0];
      if (original) {
        original.width = cellW;
        original.height = cellH;
      }

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (r === 0 && c === 0) continue;
          createNativeShape(slide, {
            geometry,
            left: src.left + c * (cellW + colGutter),
            top: src.top + r * (cellH + rowGutter),
            width: cellW,
            height: cellH,
            rotation: src.rotation,
            fill: src.fill,
            line: src.line,
            text: src.text,
            textFormat: src.textFormat,
          });
        }
      }

      await context.sync();
    });
  } catch (err) {
    throw toFriendlyShapeError(err);
  }
}
